import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InitiatePaymentRequestBody } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import { useSetAtom } from "jotai";

import EventDispatcher from "../../../utils/event-dispatcher";
import {
  COINFLOW_PAYMENT_PROVIDER,
  EventTypes,
  ROUTES,
  TopupOrigins,
  usePlayGameMetaDescription,
} from "../../../utils/constants";
import PaymentStatusTemplate, {
  PaymentStatusType,
} from "../../../components/Payments/PaymentStatusTemplate/PaymentStatusTemplate";
import { setStoredTopupOrigin } from "../../../utils/storage";
import { handleInitiatePayment } from "../../../api-clients/walletsPaymentsClient";
import Coinflow from "../../../components/Payments/Coinflow/Coinflow";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../../utils/renderHeaders";
import XCircleIcon from "../../../components/icons/XCircleIcon";
import LoadingSpinner from "../../../components/Payments/LoadingSpinner/LoadingSpinner";
import { InitiatePaymentStates } from "../../../components/Payments/types";
import usePayments, {
  MAX_PAYMENT_RETRIES,
  PaymentInitiationUrlParams,
  generatePaymentParams,
} from "../../../hooks/usePayments";
import useAnalytics from "../../../hooks/useAnalytics";
import { topupInfoAtom } from "../../../jotai/atoms/topup";

const initiatePaymentStateDetails = {
  [InitiatePaymentStates.INITIATING]: {
    paymentStatusType: PaymentStatusType.LOADING,
    title: "Initiating payment. Please wait...",
    eventType: undefined,
    icon: <LoadingSpinner />,
  },
  [InitiatePaymentStates.INITIATING_FAILED]: {
    paymentStatusType: PaymentStatusType.DONE,
    title: "Payment initiation failed",
    eventType: EventTypes.PAYMENTS.FAILED,
    icon: <XCircleIcon />,
  },
  [InitiatePaymentStates.INVALID_URL]: {
    paymentStatusType: PaymentStatusType.DONE,
    title: "Invalid payment URL. Please try again.",
    eventType: EventTypes.PAYMENTS.ERROR,
    icon: <XCircleIcon />,
  },
};

function PaymentInitiationMobile() {
  const analytics = useAnalytics();
  const [queryParams] = useSearchParams();
  const metaDescriptions = usePlayGameMetaDescription();
  const { t } = useTranslation();
  const setTopupInfo = useSetAtom(topupInfoAtom);
  const {
    paymentRetries,
    updatePaymentRetries,
    getPaymentRetryCount,
    generatePaymentRetryData,
    deletePaymentRetryData,
  } = usePayments();
  const [initiatePaymentState, setInitiatePaymentState] =
    useState<InitiatePaymentStates>(InitiatePaymentStates.INITIATING);
  const isCoinflowPage =
    queryParams.get(PaymentInitiationUrlParams.PROVIDER) ===
      COINFLOW_PAYMENT_PROVIDER &&
    !!queryParams.get(PaymentInitiationUrlParams.DATA);

  const generatePaymentLink = useCallback(
    async (
      paymentData: InitiatePaymentRequestBody,
      paymentRetryCount: number,
    ) => {
      const { planId, provider, userId, isSpecialOffer } = paymentData;
      const requestBody = { ...paymentData, isRetry: !!paymentRetryCount };
      const initiatePaymentResponse = await handleInitiatePayment(requestBody);

      if (!initiatePaymentResponse) {
        setInitiatePaymentState(InitiatePaymentStates.INITIATING_FAILED);
        updatePaymentRetries({
          ...paymentRetries,
          [planId]: generatePaymentRetryData(paymentRetryCount + 1),
        });
        return;
      }
      EventDispatcher.dispatch(EventTypes.PAYMENTS.INITIATED, {
        message: `Redirecting to ${provider}`,
      });
      deletePaymentRetryData(planId);
      analytics.sendPaystackRedirectEvent({
        payment_link: window.location.href,
        plan_id: planId,
        payment_link_user_id: userId,
        is_special_offer: isSpecialOffer,
        is_retry: requestBody.isRetry,
        retry_count: paymentRetryCount,
      });
      window.location.href = initiatePaymentResponse.url;
    },
    [
      analytics,
      paymentRetries,
      updatePaymentRetries,
      generatePaymentRetryData,
      deletePaymentRetryData,
    ],
  );

  useEffect(() => {
    !isCoinflowPage && setStoredTopupOrigin(TopupOrigins.MOBILE);

    const { topUpUniqueId, ...paymentParams } =
      generatePaymentParams(queryParams);
    analytics.sendInitiatingTopupPageOpenedEvent({
      payment_link: window.location.href,
      plan_id: paymentParams.planId,
      payment_link_user_id: paymentParams.userId,
      is_special_offer: paymentParams.isSpecialOffer,
    });
    const paramValues = Object.values(paymentParams);
    if (paramValues.includes("") || paramValues.includes(null)) {
      setInitiatePaymentState(InitiatePaymentStates.INVALID_URL);
      EventDispatcher.dispatch(EventTypes.PAYMENTS.ERROR, {
        message: initiatePaymentStateDetails[InitiatePaymentStates.INVALID_URL],
      });
      return;
    }

    const planId = paymentParams.planId as string;
    const paymentRetryCount = getPaymentRetryCount(planId);

    if (paymentRetryCount >= MAX_PAYMENT_RETRIES) {
      setInitiatePaymentState(InitiatePaymentStates.INITIATING_FAILED);
      EventDispatcher.dispatch(EventTypes.PAYMENTS.FAILED, {
        message:
          initiatePaymentStateDetails[InitiatePaymentStates.INITIATING_FAILED],
      });
      deletePaymentRetryData(planId);
      return;
    }

    setTopupInfo((prevInfo) => ({
      ...prevInfo,
      isSpecialOffer: paymentParams.isSpecialOffer,
      topUpUniqueId,
    }));
    generatePaymentLink(
      paymentParams as InitiatePaymentRequestBody,
      paymentRetryCount,
    ).catch();
  }, [
    analytics,
    queryParams,
    isCoinflowPage,
    generatePaymentLink,
    getPaymentRetryCount,
    deletePaymentRetryData,
    setTopupInfo,
  ]);

  if (isCoinflowPage) return <Coinflow />;

  return (
    <>
      {renderHeaders({
        title: t("metadata:payment_initiation_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:payment_initiation_hyper_gaming_app"),
        ),
      })}

      <PaymentStatusTemplate
        type={
          initiatePaymentStateDetails[initiatePaymentState].paymentStatusType
        }
        title={initiatePaymentStateDetails[initiatePaymentState].title}
        eventType={initiatePaymentStateDetails[initiatePaymentState].eventType}
        navigateUrl={ROUTES.WALLET.TOPUP_FAILED}
        icon={initiatePaymentStateDetails[initiatePaymentState].icon}
      />
    </>
  );
}

export default PaymentInitiationMobile;
